import { useMemo } from 'react'
import api from '../../lib/commons/api'
import { UserApiTokenRepository } from '../../applications/ports/userApiTokenRepository'
import {
  UserApiTokenEntity,
  CreateUserApiTokenInput,
  CreateUserApiTokenOutput,
  RevokeUserApiTokenOutput,
} from '../../domain/entity/UserApiTokenEntity'
import { ApiTokenScope } from '../../domain/entity/ApiTokenScope'
import { ReferencedEntityWithIcon } from '../../domain/value-object/ReferencedEntity'

type UserApiTokenResponse = {
  uuid: string
  tokenName: string
  expiredAt: string
  revoked: boolean
  expired: boolean
  scopes: string[]
  lastUsedAt: string | null
  createdAt: string
  owner?: ReferencedEntityWithIcon
  createdBy?: ReferencedEntityWithIcon
}

const toEntity = (response: UserApiTokenResponse): UserApiTokenEntity => ({
  uuid: response.uuid,
  tokenName: response.tokenName,
  expiredAt: new Date(response.expiredAt).getTime(),
  revoked: response.revoked,
  expired: response.expired,
  scopes: response.scopes,
  lastUsedAt: response.lastUsedAt
    ? new Date(response.lastUsedAt).getTime()
    : null,
  createdAt: new Date(response.createdAt).getTime(),
  owner: response.owner,
  createdBy: response.createdBy,
})

const toRequest = (input: CreateUserApiTokenInput) => ({
  uuid: input.uuid,
  tokenName: input.tokenName,
  expiredAt: new Date(input.expiredAt).toISOString(),
  scopes: input.scopes,
})

export const useUserApiTokenRepository = (): UserApiTokenRepository => {
  return useMemo(
    () => ({
      getOwn: async (): Promise<UserApiTokenEntity[]> => {
        const response = await api.functional.request(
          'GET',
          '/api/v1/user_api_tokens'
        )
        const data = response.json as UserApiTokenResponse[]
        return data.map(toEntity)
      },
      getAll: async (): Promise<UserApiTokenEntity[]> => {
        const response = await api.functional.request(
          'GET',
          '/api/v1/user_api_tokens/all'
        )
        const data = response.json as UserApiTokenResponse[]
        return data.map(toEntity)
      },
      getScopes: async (): Promise<ApiTokenScope[]> => {
        const response = await api.functional.request(
          'GET',
          '/api/v1/user_api_tokens/scopes'
        )
        return response.json as ApiTokenScope[]
      },
      create: async (
        input: CreateUserApiTokenInput
      ): Promise<CreateUserApiTokenOutput> => {
        const response = await api.functional.request(
          'POST',
          '/api/v1/user_api_tokens',
          toRequest(input)
        )
        return response.json as CreateUserApiTokenOutput
      },
      revoke: async (uuid: string): Promise<RevokeUserApiTokenOutput> => {
        const response = await api.functional.request(
          'POST',
          `/api/v1/user_api_tokens/revoke/${uuid}`
        )
        return response.json as RevokeUserApiTokenOutput
      },
    }),
    []
  )
}
